'use client';

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiErrorWarningLine, RiLoader4Line } from '@remixicon/react';
import { useLocale } from 'next-intl';

import { LanguageMenu } from '@/components/language-menu';
import { RiArrowLeftLine } from '@/components/ui/rtl-icons';
import { Logo } from '@/components/logo';
import { Link } from '@/i18n/navigation';
import { apiBaseUrl } from '@/lib/api';
import { dur, ease, spring } from '@/lib/motion';
import { siteLinks } from '@/lib/site-links';

/**
 * Giriş / kayıt / kod adımı ekranlarının ortak parçaları.
 *
 * Neden ayrı bir dosya: formların alan kutusu, hata satırı, gönder butonu ve
 * Google yolu AYNI. Kopya tutulsaydı bir davranışı düzeltmek her dosyada
 * düzeltmek olurdu — ve kaçınılmaz olarak biri unutulurdu. Buradaki parçalar
 * durum tutmuyor (odak bayrağı dışında); doğrulama, bekleme ve hata metinleri
 * her zaman çağıran formun elinde kalıyor.
 *
 * 10 Eki 2026: parola kalktı — göz düğmesi (`PasswordReveal`) ve şifre gücü
 * ölçeri (`PasswordStrength`) bu dosyadan çıktı.
 *
 * Görünüm CSS'te: `globals.css` → "Giriş ekranı" bloğu, `.auth-*` sınıfları.
 * Buradaki bileşenler sınıf adı basar, ölçü yazmaz.
 */

/**
 * `layout` taşıyan öğelerin giriş varyantı — sahne girişinin y ötelemesiz hâli.
 *
 * Neden ayrı: bir öğede hem `layout` hem de y ötelemeli giriş çalıştırmak,
 * Motion'ın ölçüm aldığı ana denk gelirse öğeyi sıçratıyor. Hata satırı
 * belirdiğinde alanları AŞAĞI İTEN hareket zaten `layout`ta; girişin ayrıca yol
 * alması gerekmiyor, "odaklanma" yeterli.
 */
export const fieldV: Variants = {
  hidden: { opacity: 0, filter: 'blur(5px)' },
  show: { opacity: 1, filter: 'blur(0px)', transition: { duration: dur.base, ease: ease.out } },
};

/* Hata satırı: girerken blur'lu değil — 12px'lik yolu da yok. Uyarı metni
   "odaklanarak" gelmemeli, anında okunmalı. */
export const errorV: Variants = {
  hidden: { opacity: 0, y: -4 },
  show: { opacity: 1, y: 0, transition: { duration: dur.fast, ease: ease.out } },
  exit: { opacity: 0, y: -4, transition: { duration: dur.instant } },
};

/**
 * Logo + geri oku. Tek bağlantı iki iş yapıyor: marka ve "siteye dön".
 * Ok logonun SOLUNDA — hover'da sola kayarak gideceği yönü gösteriyor
 * (kayma CSS'te, `.auth-back:hover .auth-back-arrow`).
 *
 * Satırın sonunda dil menüsü (29 Eyl 2026, 13 dil). Eşik ekranlarında
 * footer yok; cihaz dilinden açılan bir kayıt formunu başka dilde doldurmak
 * isteyen kişinin tek yolu bu. Hesabın dili kayıt anındaki arayüz dili
 * oluyor (`register-form.tsx`), yani seçim burada yapılmalı.
 */
export function AuthBrand({ href = siteLinks.home.href }: { href?: string }) {
  return (
    <div className="auth-brand">
      <Link href={href} className="auth-back">
        <RiArrowLeftLine className="auth-back-arrow" size={16} aria-hidden="true" />
        <Logo className="auth-logo" />
      </Link>
      <LanguageMenu placement="bottom" />
    </div>
  );
}

/**
 * Hızlı yol. Formun ÜSTÜNDE duruyor: Google ile geçen kullanıcı alanlara hiç
 * bakmadan geçsin. Altta olsaydı önce alanları okuyup sonra "aslında daha kısası
 * varmış" demek zorunda kalırdı.
 *
 * Tıklama TAM SAYFA gidiş: `GET {API}/auth/google/start` Google'a 302 veriyor,
 * Google API'nin dönüş ucuna, o da çerezleri yazıp panele (ya da hata koduyla
 * `/login`e). Giriş ve kayıt aynı uç — hesap yoksa açılıyor.
 *
 * Bekleme hâli: logo yerinde çarka dönüyor, düğme kilitleniyor — Google'ın
 * sayfası gelene kadar geçen yarım saniyede ikinci tıklama ikinci bir akış
 * başlatmasın. Geri tuşuyla dönülürse (bfcache) sayfa donmuş hâliyle
 * geliyor; `pageshow` kilidi açıyor.
 */
export function GoogleButton({ label, next }: { label: string; next?: string }) {
  const locale = useLocale();
  const reduceMotion = useReducedMotion();
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const reset = (event: PageTransitionEvent) => {
      if (event.persisted) setPending(false);
    };
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  function go() {
    if (pending) return;
    setPending(true);
    const params = new URLSearchParams({ locale });
    if (next) params.set('next', next);
    window.location.assign(`${apiBaseUrl()}/auth/google/start?${params.toString()}`);
  }

  return (
    <motion.button
      type="button"
      className="auth-social"
      onClick={go}
      disabled={pending}
      aria-busy={pending}
      whileHover={pending ? undefined : { scale: 1.01, y: -1 }}
      whileTap={pending ? undefined : { scale: 0.99 }}
      transition={spring.snap}
    >
      <span className="auth-social-logo" aria-hidden="true">
        <AnimatePresence mode="wait" initial={false}>
          {pending ? (
            <motion.span
              key="spin"
              className="auth-social-spinner"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={reduceMotion ? { opacity: 1, scale: 1 } : { opacity: 1, scale: 1, rotate: 360 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: dur.instant },
                scale: { duration: dur.instant },
                rotate: { duration: 0.9, repeat: Infinity, ease: 'linear' },
              }}
            >
              <RiLoader4Line size={18} />
            </motion.span>
          ) : (
            <motion.img
              key="logo"
              src="/logos/brands/google.svg"
              alt=""
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: dur.instant, ease: ease.out }}
            />
          )}
        </AnimatePresence>
      </span>
      {label}
    </motion.button>
  );
}

export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="auth-divider">
      <span className="auth-divider-line" aria-hidden="true" />
      <span className="auth-divider-text">{label}</span>
      <span className="auth-divider-line" aria-hidden="true" />
    </div>
  );
}

/**
 * Alan kutusu: ikon + girdi + (varsa) sağdaki düğme.
 *
 * Odakta değişen tek şey kutunun kendisi — kenarlık tema rengine dönüyor,
 * zemindeki yeşil sis koyulaşıyor. Dışarıda ayrı bir odak halkası, altta ayrı
 * bir çizgi yok; üçü aynı anda "buradasın" deyince kutu üç kat kalınlaşmış gibi
 * görünüyordu. Renk geçişleri CSS'te, düzen ölçmüyorlar.
 *
 * `:focus-within` yerine state: ikon rengi de aynı bayrağa bağlı ve tek
 * kaynaktan okunuyor.
 */
export function AuthField({
  invalid,
  children,
}: {
  invalid: boolean;
  children: ReactNode;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div
      className="auth-input-wrap"
      data-invalid={invalid || undefined}
      data-focused={focused || undefined}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </div>
  );
}

/** Alan altı hata satırı. Yer kaplamayan bir kutu değil — yokken hiç yok,
    varken altındakileri `layout` ile aşağı itiyor. */
export function FieldError({ id, message }: { id: string; message?: string }) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {message && (
        <motion.p
          key="error"
          id={id}
          className="auth-field-error"
          variants={errorV}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          {message}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

/**
 * Form geneli hata. Sarmalayıcı kutu YOK: yokken hiç yer kaplamasın (boş bir
 * div bile flex `gap`ini tüketip çift boşluk bırakıyordu), AnimatePresence'ın
 * kendisi DOM'a düğüm basmıyor.
 *
 * `role="alert"` — `aria-live` değil: canlı bölge, içeriği değişmeden ÖNCE
 * DOM'da olmak zorunda; burada eleman sonradan basılıyor. `alert` eklendiği an
 * okunuyor. Alan hataları ayrıca canlı yapılmadı, onlar `aria-describedby` ile
 * alana bağlı.
 */
export function FormAlert({ message }: { message?: string }) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {message && (
        <motion.p
          key="form-error"
          role="alert"
          className="auth-alert"
          variants={errorV}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          <RiErrorWarningLine size={16} aria-hidden="true" />
          {message}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

/**
 * Gönder butonu.
 *
 * 🔑 İçindeki bekleme çarkı sonsuz döngü: `useReducedMotion()` ile elle
 * kapatılıyor. MotionConfig'in `reducedMotion="user"` ayarı döngüleri
 * durdurmuyor (DESIGN.md §1). Tercih açıkken çark sabit duruyor; "bekleniyor"
 * bilgisini zaten butonun metni ve `aria-busy` taşıyor.
 */
export function SubmitButton({
  pending,
  label,
  pendingLabel,
}: {
  pending: boolean;
  label: string;
  pendingLabel: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.button
      type="submit"
      className="auth-submit"
      disabled={pending}
      aria-busy={pending}
      whileHover={pending ? undefined : { scale: 1.01, y: -1 }}
      whileTap={pending ? undefined : { scale: 0.985 }}
      transition={spring.snap}
    >
      {/* Metin de ikon da yerinde değişiyor; buton genişliği sabit kalsın diye
          ikisi de aynı satırda, `mode="wait"` ile. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={pending ? 'pending' : 'idle'}
          className="auth-submit-label"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: dur.instant, ease: ease.out }}
        >
          {pending && (
            <motion.span
              className="auth-submit-spinner"
              aria-hidden="true"
              animate={reduceMotion ? undefined : { rotate: 360 }}
              transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
            >
              <RiLoader4Line size={18} />
            </motion.span>
          )}
          {pending ? pendingLabel : label}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
}

/**
 * Yasal satırın bağlantı üreticileri — `t.rich('legal', legalChunks)`.
 *
 * Metin her formda farklı ("devam ederek…" / "hesap oluşturarak…"), gidilen
 * iki adres aynı. Ortak olan burada duruyor, değişen çeviri dosyasında.
 */
export const legalChunks = {
  terms: (chunks: ReactNode) => (
    <Link href="/terms" className="auth-legal-link">
      {chunks}
    </Link>
  ),
  privacy: (chunks: ReactNode) => (
    <Link href="/privacy" className="auth-legal-link">
      {chunks}
    </Link>
  ),
  /* Satış belgeleri yalnız KAYIT metninde geçiyor (giriş cümlesi onları
     anmıyor) ve orada bile "kabul ettiniz" demiyor: kayıt bir satış değil,
     ücretsiz deneme alanı açıyor. Cümle "ücretli plana geçtiğinizde geçerli
     olur" diyor — kabul, ödeme ekranındaki kutuda alınıyor. Kullanılmayan
     chunk zararsız; `t.rich` yalnız metinde geçeni çağırıyor. */
  preInformation: (chunks: ReactNode) => (
    <Link href="/pre-information" className="auth-legal-link">
      {chunks}
    </Link>
  ),
  distanceSales: (chunks: ReactNode) => (
    <Link href="/distance-sales" className="auth-legal-link">
      {chunks}
    </Link>
  ),
};
